/**
 * A/B Test Configuration Form
 *
 * Form for configuring and creating a new A/B test.
 * Uses accordion pattern matching RLLoopConfigForm.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as StartIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Science as SandboxIcon,
  Public as LiveIcon
} from '@mui/icons-material';
import {
  getAdvancedSimulationClientPersonas,
  getAdvancedSimulationTherapistPersonas,
  getReviewerPersonas,
  createABTest,
  updateABTest,
  startABTest
} from '../../../services/advancedSimulationApi';
import { listSandboxPersonas } from '../../../services/researcherApi';

function ABTestConfigForm({ token, onTestCreated, onTestUpdated, isResearcher = false, initialData = null, editMode = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liveClientPersonas, setLiveClientPersonas] = useState([]);
  const [liveTherapistPersonas, setLiveTherapistPersonas] = useState([]);
  const [sandboxClientPersonas, setSandboxClientPersonas] = useState([]);
  const [sandboxTherapistPersonas, setSandboxTherapistPersonas] = useState([]);
  const [reviewerPersonas, setReviewerPersonas] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(true);

  // Persona filter for researchers
  const [personaFilter, setPersonaFilter] = useState('all');

  // Basic Info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [testType, setTestType] = useState('prompt_version');

  // Variants
  const [variants, setVariants] = useState([
    { name: 'Control', description: '', isControl: true, basePersonaKey: '', promptOverride: '', therapistPersonaKey: '', modality: '' },
    { name: 'Variant B', description: '', isControl: false, basePersonaKey: '', promptOverride: '', therapistPersonaKey: '', modality: '' }
  ]);

  // Client Personas
  const [clientPersonaKeys, setClientPersonaKeys] = useState([]);
  const [conditionFilter, setConditionFilter] = useState([]);
  const [severityFilter, setSeverityFilter] = useState([]);

  // Execution Config
  const [runsPerVariantPerClient, setRunsPerVariantPerClient] = useState(5);
  const [maxTurnsPerRun, setMaxTurnsPerRun] = useState(50);
  const [confidenceLevel, setConfidenceLevel] = useState(0.95);
  const [reviewerPersonaId, setReviewerPersonaId] = useState('');
  const [parallelism, setParallelism] = useState(3);

  // Load personas on mount
  useEffect(() => {
    const loadPersonas = async () => {
      try {
        const [clientRes, therapistRes, reviewerRes] = await Promise.all([
          getAdvancedSimulationClientPersonas(token),
          getAdvancedSimulationTherapistPersonas(token),
          getReviewerPersonas(token)
        ]);
        setLiveClientPersonas(clientRes.personas || []);
        setLiveTherapistPersonas(therapistRes.personas || []);
        setReviewerPersonas(reviewerRes.personas || []);

        if (isResearcher) {
          try {
            const sandboxRes = await listSandboxPersonas();
            const clientSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
              .filter(p => p.personaType === 'client');
            const therapistSandbox = (sandboxRes.owned || []).concat(sandboxRes.shared || [])
              .filter(p => p.personaType === 'therapist');
            setSandboxClientPersonas(clientSandbox);
            setSandboxTherapistPersonas(therapistSandbox);
          } catch (sandboxErr) {
            console.error('Failed to load sandbox personas:', sandboxErr);
          }
        }
      } catch (err) {
        setError('Failed to load personas');
        console.error(err);
      } finally {
        setLoadingPersonas(false);
      }
    };
    loadPersonas();
  }, [token, isResearcher]);

  // Populate form from initialData in edit mode
  useEffect(() => {
    if (!initialData) return;
    if (initialData.name) setName(initialData.name);
    if (initialData.description != null) setDescription(initialData.description);
    if (initialData.hypothesis != null) setHypothesis(initialData.hypothesis);
    if (initialData.testType) setTestType(initialData.testType);
    if (initialData.variants?.length >= 2) {
      const controlId = initialData.controlVariantId;
      setVariants(initialData.variants.map(v => ({
        name: v.name || '',
        description: v.description || '',
        isControl: v.variantId === controlId,
        basePersonaKey: v.basePersonaKey || '',
        promptOverride: v.promptOverride || '',
        therapistPersonaKey: v.therapistPersonaKey || '',
        modality: v.modality || '',
        variantId: v.variantId || null
      })));
    }
    if (initialData.clientConfig?.clientPersonaKeys) setClientPersonaKeys(initialData.clientConfig.clientPersonaKeys);
    if (initialData.clientConfig?.conditionFilter) setConditionFilter(initialData.clientConfig.conditionFilter);
    if (initialData.clientConfig?.severityFilter) setSeverityFilter(initialData.clientConfig.severityFilter);
    if (initialData.executionConfig?.runsPerVariantPerClient) setRunsPerVariantPerClient(initialData.executionConfig.runsPerVariantPerClient);
    if (initialData.executionConfig?.maxTurnsPerRun) setMaxTurnsPerRun(initialData.executionConfig.maxTurnsPerRun);
    if (initialData.executionConfig?.confidenceLevel) setConfidenceLevel(initialData.executionConfig.confidenceLevel);
    if (initialData.executionConfig?.reviewerPersonaId) setReviewerPersonaId(initialData.executionConfig.reviewerPersonaId);
    if (initialData.executionConfig?.parallelism) setParallelism(initialData.executionConfig.parallelism);
  }, [initialData]);

  const getFilteredClientPersonas = () => {
    const live = liveClientPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxClientPersonas.map(p => ({ ...p, source: 'sandbox' }));
    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live];
  };

  const getFilteredTherapistPersonas = () => {
    const live = liveTherapistPersonas.map(p => ({ ...p, source: 'live' }));
    const sandbox = sandboxTherapistPersonas.map(p => ({ ...p, source: 'sandbox' }));
    if (!isResearcher || personaFilter === 'live') return live;
    if (personaFilter === 'sandbox') return sandbox;
    return [...sandbox, ...live];
  };

  const clientPersonas = getFilteredClientPersonas();
  const therapistPersonas = getFilteredTherapistPersonas();

  const updateVariant = (index, field, value) => {
    setVariants(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const setControlVariant = (index) => {
    setVariants(prev => prev.map((v, i) => ({ ...v, isControl: i === index })));
  };

  const addVariant = () => {
    if (variants.length >= 5) return;
    const letter = String.fromCharCode(65 + variants.length); // C, D, E
    setVariants(prev => [...prev, {
      name: `Variant ${letter}`,
      description: '',
      isControl: false,
      basePersonaKey: '',
      promptOverride: '',
      therapistPersonaKey: '',
      modality: ''
    }]);
  };

  const removeVariant = (index) => {
    if (variants.length <= 2) return;
    setVariants(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // If we removed the control, make the first one control
      if (!updated.some(v => v.isControl)) {
        updated[0].isControl = true;
      }
      return updated;
    });
  };

  const buildConfig = () => {
    const variantConfigs = variants.map(v => {
      const base = {
        name: v.name,
        description: v.description
      };
      if (editMode && v.variantId) {
        base.variantId = v.variantId;
      }
      if (testType === 'prompt_version') {
        base.basePersonaKey = v.basePersonaKey;
        if (v.promptOverride) base.promptOverride = v.promptOverride;
      } else {
        base.therapistPersonaKey = v.therapistPersonaKey;
        if (v.modality) base.modality = v.modality;
      }
      return base;
    });

    const controlIndex = variants.findIndex(v => v.isControl);

    return {
      name,
      description,
      hypothesis,
      testType,
      variants: variantConfigs,
      controlVariantIndex: controlIndex >= 0 ? controlIndex : 0,
      clientConfig: {
        clientPersonaKeys,
        ...(conditionFilter.length > 0 && { conditionFilter }),
        ...(severityFilter.length > 0 && { severityFilter })
      },
      executionConfig: {
        runsPerVariantPerClient,
        maxTurnsPerRun,
        confidenceLevel,
        ...(reviewerPersonaId && { reviewerPersonaId }),
        parallelism
      }
    };
  };

  const handleCreateDraft = async () => {
    setLoading(true);
    setError(null);
    try {
      const config = buildConfig();
      const result = await createABTest(config, token);
      const test = result.test || result;
      onTestCreated?.(test);
    } catch (err) {
      setError(err.error || err.message || 'Failed to create test');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const config = buildConfig();
      const result = await createABTest(config, token);
      const test = result.test || result;
      if (test?._id) {
        await startABTest(test._id, token);
      }
      onTestCreated?.(test);
    } catch (err) {
      setError(err.error || err.message || 'Failed to create and start test');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError(null);
    try {
      const config = buildConfig();
      const result = await updateABTest(initialData._id, config, token);
      const test = result.test || result;
      onTestUpdated?.(test);
    } catch (err) {
      setError(err.error || err.message || 'Failed to update test');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAndStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const config = buildConfig();
      const result = await updateABTest(initialData._id, config, token);
      const test = result.test || result;
      if (test?._id) {
        await startABTest(test._id, token);
      }
      onTestUpdated?.(test);
    } catch (err) {
      setError(err.error || err.message || 'Failed to update and start test');
    } finally {
      setLoading(false);
    }
  };

  const isValid = name && variants.length >= 2 && clientPersonaKeys.length > 0 &&
    variants.every(v => v.name && (
      testType === 'prompt_version' ? v.basePersonaKey : v.therapistPersonaKey
    ));

  const totalEstimatedRuns = variants.length * clientPersonaKeys.length * runsPerVariantPerClient;

  if (loadingPersonas) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        {editMode ? 'Edit A/B Test' : 'Configure A/B Test'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {editMode
          ? 'Modify the test configuration. Changes can only be saved while the test is in draft status.'
          : 'Set up a controlled comparison between prompt versions or therapeutic modalities.'}
      </Typography>

      {/* Persona Filter for Researchers */}
      {isResearcher && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Persona Source Filter
          </Typography>
          <ToggleButtonGroup
            value={personaFilter}
            exclusive
            onChange={(e, value) => value && setPersonaFilter(value)}
            size="small"
          >
            <ToggleButton value="all">All Personas</ToggleButton>
            <ToggleButton value="sandbox">
              <SandboxIcon sx={{ mr: 0.5 }} fontSize="small" />
              Sandbox Only
            </ToggleButton>
            <ToggleButton value="live">
              <LiveIcon sx={{ mr: 0.5 }} fontSize="small" />
              Live Only
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Section 1: Basic Info */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Basic Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Test Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g., MBT vs CBT Empathy Comparison"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                minRows={2}
                placeholder="Describe what this test is evaluating"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hypothesis"
                value={hypothesis}
                onChange={(e) => setHypothesis(e.target.value)}
                multiline
                minRows={2}
                placeholder="e.g., The revised MBT prompt will score 10%+ higher on empathy dimensions"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Test Type
              </Typography>
              <ToggleButtonGroup
                value={testType}
                exclusive
                onChange={(e, value) => {
                  if (value) setTestType(value);
                }}
                size="small"
              >
                <ToggleButton value="prompt_version">Prompt Version</ToggleButton>
                <ToggleButton value="modality_comparison">Modality Comparison</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {testType === 'prompt_version'
                  ? 'Compare different prompt versions of the same persona'
                  : 'Compare different therapeutic modalities/personas'}
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 2: Variants */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">
            Variants ({variants.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define at least 2 variants to compare. Mark one as the control.
          </Typography>

          {variants.map((variant, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={variant.isControl}
                          onChange={() => setControlVariant(index)}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption">Control</Typography>}
                    />
                    {variant.isControl && (
                      <Chip label="Control" size="small" color="primary" />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeVariant(index)}
                    disabled={variants.length <= 2}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Variant Name"
                      value={variant.name}
                      onChange={(e) => updateVariant(index, 'name', e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      value={variant.description}
                      onChange={(e) => updateVariant(index, 'description', e.target.value)}
                    />
                  </Grid>

                  {testType === 'prompt_version' ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Base Persona</InputLabel>
                          <Select
                            value={variant.basePersonaKey}
                            onChange={(e) => updateVariant(index, 'basePersonaKey', e.target.value)}
                            label="Base Persona"
                          >
                            {therapistPersonas.map((p) => (
                              <MenuItem key={`${p.source}-${p.key}`} value={p.key}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {p.source === 'sandbox' && (
                                    <Chip label="SB" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                  {p.name} ({p.key})
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Prompt Override"
                          value={variant.promptOverride}
                          onChange={(e) => updateVariant(index, 'promptOverride', e.target.value)}
                          multiline
                          minRows={3}
                          maxRows={6}
                          placeholder="Leave empty to use the persona's default prompt, or provide an override"
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Therapist Persona</InputLabel>
                          <Select
                            value={variant.therapistPersonaKey}
                            onChange={(e) => updateVariant(index, 'therapistPersonaKey', e.target.value)}
                            label="Therapist Persona"
                          >
                            {therapistPersonas.map((p) => (
                              <MenuItem key={`${p.source}-${p.key}`} value={p.key}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {p.source === 'sandbox' && (
                                    <Chip label="SB" size="small" color="secondary" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                  {p.name} ({p.key})
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Modality"
                          value={variant.modality}
                          onChange={(e) => updateVariant(index, 'modality', e.target.value)}
                          placeholder="e.g., MBT, CBT, DBT"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={addVariant}
            disabled={variants.length >= 5}
            variant="outlined"
            size="small"
          >
            Add Variant ({variants.length}/5)
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Section 3: Client Personas */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Client Personas</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select client personas to test each variant against.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Client Personas</InputLabel>
                <Select
                  multiple
                  value={clientPersonaKeys}
                  onChange={(e) => setClientPersonaKeys(e.target.value)}
                  input={<OutlinedInput label="Client Personas" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {clientPersonas.map((persona) => (
                    <MenuItem key={`${persona.source}-${persona.key}`} value={persona.key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {persona.source === 'sandbox' && (
                          <Chip label="Sandbox" size="small" color="secondary" sx={{ height: 20 }} />
                        )}
                        {persona.name} ({persona.key})
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Condition Filter (Optional)</InputLabel>
                <Select
                  multiple
                  value={conditionFilter}
                  onChange={(e) => setConditionFilter(e.target.value)}
                  input={<OutlinedInput label="Condition Filter (Optional)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}
                >
                  {['anxiety', 'depression', 'ptsd', 'ocd', 'bipolar', 'personality_disorder'].map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Severity Filter (Optional)</InputLabel>
                <Select
                  multiple
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  input={<OutlinedInput label="Severity Filter (Optional)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}
                >
                  {['mild', 'moderate', 'severe'].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 4: Execution Config */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Execution Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Runs per Variant per Client"
                value={runsPerVariantPerClient}
                onChange={(e) => setRunsPerVariantPerClient(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 50 }}
                helperText="More runs = better statistical power"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Turns per Run"
                value={maxTurnsPerRun}
                onChange={(e) => setMaxTurnsPerRun(Math.max(10, Math.min(200, parseInt(e.target.value) || 10)))}
                inputProps={{ min: 10, max: 200 }}
                helperText="Conversation length limit"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Confidence Level</InputLabel>
                <Select
                  value={confidenceLevel}
                  onChange={(e) => setConfidenceLevel(e.target.value)}
                  label="Confidence Level"
                >
                  <MenuItem value={0.90}>90%</MenuItem>
                  <MenuItem value={0.95}>95% (Standard)</MenuItem>
                  <MenuItem value={0.99}>99%</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Reviewer Persona (Optional)</InputLabel>
                <Select
                  value={reviewerPersonaId}
                  onChange={(e) => setReviewerPersonaId(e.target.value)}
                  label="Reviewer Persona (Optional)"
                >
                  <MenuItem value="">
                    <em>Generic Reviewer (Default)</em>
                  </MenuItem>
                  {reviewerPersonas.map((persona) => (
                    <MenuItem key={persona._id} value={persona._id}>
                      <Box>
                        <Typography variant="body2">{persona.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {persona.description?.substring(0, 60)}...
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Parallelism"
                value={parallelism}
                onChange={(e) => setParallelism(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 10 }}
                helperText="Concurrent simulation runs"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Section 5: Summary & Submit */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Summary & Submit</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Test Type</Typography>
                <Typography variant="body2">{testType === 'prompt_version' ? 'Prompt Version' : 'Modality Comparison'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Variants</Typography>
                <Typography variant="body2">{variants.length}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Client Personas</Typography>
                <Typography variant="body2">{clientPersonaKeys.length}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Runs per Variant per Client</Typography>
                <Typography variant="body2">{runsPerVariantPerClient}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Total Estimated Runs</Typography>
                <Typography variant="h6" color="primary">{totalEstimatedRuns}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Confidence Level</Typography>
                <Typography variant="body2">{(confidenceLevel * 100).toFixed(0)}%</Typography>
              </Grid>
            </Grid>
          </Box>

          {!isValid && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please fill in all required fields: test name, at least 2 variants with personas, and at least 1 client persona.
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {editMode ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleUpdate}
                  disabled={!isValid || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUpdateAndStart}
                  disabled={!isValid || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <StartIcon />}
                >
                  {loading ? 'Saving...' : 'Save & Start'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCreateDraft}
                  disabled={!isValid || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  {loading ? 'Creating...' : 'Create as Draft'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateAndStart}
                  disabled={!isValid || loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <StartIcon />}
                >
                  {loading ? 'Creating...' : 'Create & Start'}
                </Button>
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default ABTestConfigForm;
